import { useSendGoogleDriveFilteToTelegram } from '@/shared/api'
import { CheckmarkBlueSVGIcon, DocumentIcon } from '@/shared/assets'
import { Command, CommandList, Input, ShadcnButton } from '@/shared/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { cn } from '@/true-fsd/shared/utils/css'
import { DocumentRecord, ORDMarkingFormData } from '@synapex/prisma/client'
import {
    ContractUnion,
    DocumentRecordWithType,
} from '@synapex/shared2/gateway-rest-api'
import { ChevronDown, EllipsisVerticalIcon, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    CustomContractActionsMenu,
    DefaultContractActionsMenu,
} from './ContractActionsMenu'
import { ContractSelectionList } from './ContractSelectionList'

type DraftFileFieldProps = {
    onSelect?: (contract: DocumentRecord) => Promise<void>
    contracts: ContractUnion[] | undefined
    markingFormData: ORDMarkingFormData
    onClickCreate: (type: 'custom' | 'default') => void
    onClickEditCustomContract?: (customContractId: number) => void
    onDeleteItem: (
        type: 'draft' | 'final',
        contract: DocumentRecord
    ) => Promise<void>
    onUpdateDefaultContractNumber?: (
        documentRecordId: number,
        contractNumber: string
    ) => Promise<void>
    onDeleteCustomContract?: () => Promise<void>
    onSelectCustomContract?: (customContractId: number) => Promise<void>
    disabled?: boolean
    error?: boolean
    isLoading?: boolean
    message: string
    createNewDocumentMessage: string
    className?: string
    canCreateContract?: boolean
    createContractTooltip?: string
    disabledControlButton?: boolean
}

export function DraftFileField({
    onSelect,
    contracts,
    markingFormData,
    onClickCreate,
    onClickEditCustomContract,
    onDeleteItem,
    onDeleteCustomContract,
    onSelectCustomContract,
    onUpdateDefaultContractNumber,
    disabled,
    error,
    isLoading,
    message,
    createNewDocumentMessage,
    className,
    canCreateContract = true,
    createContractTooltip,
    disabledControlButton,
}: DraftFileFieldProps) {
    const [open, setOpen] = useState(false)
    const [editingDefaultContractId, setEditingDefaultContractId] = useState<
        number | null
    >(null)
    const [editingDefaultContractValue, setEditingDefaultContractValue] =
        useState('')
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [selectedItem, setSelectedItem] = useState<ContractUnion | null>(null)

    const sendToTelegram = useSendGoogleDriveFilteToTelegram()
    const editingInputRef = useRef<HTMLInputElement | null>(null)

    const draftContracts = useMemo(() => {
        return contracts?.filter((contract) => {
            if (contract.type === 'custom') {
                return (
                    contract.custom_contract_id ===
                        markingFormData.custom_contract_id ||
                    contract.ord_marking_form_data.some(
                        (formData) => !formData.contract_flag
                    ) ||
                    contract.ord_marking_form_data.length === 0
                )
            }
            return (
                (contract.status === 'draft' &&
                    !contract.has_final &&
                    contract.draft_ord_marking_form_data_contract.some(
                        (formData) => !formData.contract_flag
                    )) ||
                contract.documentRecordId === markingFormData.draft_contract_id
            )
        })
    }, [
        contracts,
        markingFormData.draft_contract_id,
        markingFormData.custom_contract_id,
    ])

    useEffect(() => {
        if (markingFormData.draft_contract_id && contracts?.length) {
            setSelectedItem(
                contracts.find(
                    (contract) =>
                        contract.type === 'default' &&
                        contract.documentRecordId ===
                            markingFormData.draft_contract_id
                ) ?? null
            )
        } else if (markingFormData.custom_contract_id && contracts?.length) {
            setSelectedItem(
                contracts.find(
                    (contract) =>
                        contract.type === 'custom' &&
                        contract.custom_contract_id ===
                            markingFormData.custom_contract_id
                ) ?? null
            )
        } else {
            setSelectedItem(null)
        }
    }, [
        contracts,
        contracts?.length,
        markingFormData.draft_contract_id,
        markingFormData.custom_contract_id,
    ])

    const selectedDefaultContract =
        selectedItem?.type === 'default' ? selectedItem : null
    const selectedCustomContract =
        selectedItem?.type === 'custom' ? selectedItem : null
    const hasSelectedContract =
        selectedDefaultContract !== null || selectedCustomContract !== null
    // const hasSelectedCustomContract =
    //     Boolean(markingFormData.custom_contract_id) && !!selectedCustomContract
    const isEditingSelectedDefaultContract =
        editingDefaultContractId !== null &&
        selectedDefaultContract?.documentRecordId === editingDefaultContractId

    useEffect(() => {
        if (isLoading) {
            setOpen(false)
        }
    }, [isLoading])

    useEffect(() => {
        if (editingDefaultContractId !== null) {
            editingInputRef.current?.focus()
            editingInputRef.current?.select()
        }
    }, [editingDefaultContractId])

    useEffect(() => {
        if (editingDefaultContractId !== null && selectedDefaultContract) {
            setEditingDefaultContractValue(
                selectedDefaultContract.custom_number ??
                    selectedDefaultContract.indexNumber?.toString()
            )
        }
    }, [editingDefaultContractId, selectedDefaultContract])

    const finalLoadingStatus = isLoading || sendToTelegram.isPending

    async function handleSelectDefaultContract(value: string) {
        const selected = contracts?.find((contract) => {
            if (contract.type === 'default') {
                return contract.documentRecordId.toString() === value
            }
            return false
        })
        if (selected && selected.type === 'default') {
            await onSelect?.(selected)
        }
        setOpen(false)
    }

    async function handleSelectCustomContract(customContractId: number) {
        await onSelectCustomContract?.(customContractId)
        setOpen(false)
    }

    function handleCancelDelete(event: React.MouseEvent) {
        event.stopPropagation()
        setShowDeleteConfirmation(false)
    }

    function handleDeleteConfirmation() {
        if (!showDeleteConfirmation) {
            setShowDeleteConfirmation(true)
        }
    }

    async function handleDelete() {
        if (selectedDefaultContract) {
            await onDeleteItem('draft', selectedDefaultContract)
        } else if (selectedCustomContract) {
            await onDeleteCustomContract?.()
        }
        setShowDeleteConfirmation(false)
    }

    function handleOpenGoogleDriveFolder(document: DocumentRecordWithType) {
        if (document.unsignedLink) {
            window.open(document.unsignedLink, '_blank')
        }
    }

    function handleSendToSelf(document: DocumentRecordWithType) {
        setOpen(false)
        sendToTelegram.mutate(document.documentRecordId)
    }

    function handleCreateContract(type: 'default' | 'custom') {
        onClickCreate(type)
        setOpen(false)
    }

    function handleEditCustomContract() {
        if (selectedCustomContract?.custom_contract_id) {
            onClickEditCustomContract?.(
                selectedCustomContract.custom_contract_id
            )
            setOpen(false)
        }
    }

    function handleEditDefaultContract() {
        if (selectedDefaultContract?.documentRecordId) {
            setEditingDefaultContractId(
                selectedDefaultContract.documentRecordId
            )
            setEditingDefaultContractValue(
                selectedDefaultContract.custom_number ??
                    selectedDefaultContract.indexNumber?.toString()
            )
            setOpen(false)
        }
    }

    function handleCancelEditDefaultContract() {
        setEditingDefaultContractId(null)
        setEditingDefaultContractValue('')
    }

    async function handleConfirmRenameDefaultContract() {
        if (
            !selectedDefaultContract ||
            !selectedDefaultContract.documentRecordId
        ) {
            return
        }

        try {
            await onUpdateDefaultContractNumber?.(
                selectedDefaultContract.documentRecordId,
                editingDefaultContractValue
            )
            handleCancelEditDefaultContract()
        } catch (error) {
            console.error(error)
        }
    }

    const renderRightControl = () => {
        if (isEditingSelectedDefaultContract) {
            return null
        }

        if (finalLoadingStatus) {
            return <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        }

        // if (disabled && hasSelectedCustomContract) {
        //     return null
        // }

        const IconComponent = hasSelectedContract
            ? EllipsisVerticalIcon
            : ChevronDown

        return (
            <div
                onClick={(event) => {
                    event.stopPropagation()
                    if (disabledControlButton) {
                        return
                    }
                    setOpen(true)
                }}
            >
                <IconComponent
                    className={cn(
                        'h-4 w-4 shrink-0',
                        disabledControlButton && 'opacity-50'
                    )}
                />
            </div>
        )
    }

    const renderTriggerLabel = () => {
        const contractLabel =
            selectedCustomContract?.number ?? selectedDefaultContract?.fileName

        if (contractLabel) {
            return (
                <div className="flex max-w-full items-center gap-1 pr-3">
                    <img src={DocumentIcon} className="h-4 w-3" />
                    <span className="flex-1 truncate">{contractLabel}</span>
                </div>
            )
        }

        return (
            <span className="mr-4 overflow-hidden text-ellipsis">
                {message}
            </span>
        )
    }

    const hasError =
        error &&
        !markingFormData.draft_contract_id &&
        !markingFormData.custom_contract_id

    const renderEditDefaultTrigger = () => {
        if (!isEditingSelectedDefaultContract) {
            return null
        }

        return (
            <div
                className={cn(
                    'flex w-56 items-center justify-between p-0 text-xs font-normal',
                    className,
                    hasError && 'border-red-500'
                )}
                onClick={(event) => event.stopPropagation()}
            >
                <Input
                    shouldShowClearButton={false}
                    innerClassName="h-[32px] pr-2"
                    disabled={isLoading}
                    ref={editingInputRef}
                    value={editingDefaultContractValue}
                    onChange={(value) => setEditingDefaultContractValue(value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                        event.stopPropagation()
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            void handleConfirmRenameDefaultContract()
                        }
                        if (event.key === 'Escape') {
                            event.preventDefault()
                            handleCancelEditDefaultContract()
                        }
                    }}
                    placeholder="Введите номер договора"
                />
                <div
                    className="absolute right-2 top-1/2 z-[1000] -translate-y-1/2 cursor-pointer"
                    onClick={handleConfirmRenameDefaultContract}
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                        <CheckmarkBlueSVGIcon className="h-4 w-4 shrink-0 text-blue-primary" />
                    )}
                </div>
            </div>
        )
    }

    const renderTrigger = () => {
        const editTrigger = renderEditDefaultTrigger()
        if (editTrigger) {
            return editTrigger
        }

        return (
            <ShadcnButton
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                    'flex h-auto w-56 items-center justify-between px-[10px] py-2 text-xs font-normal',
                    className,
                    hasError && 'border-red-500'
                )}
            >
                {renderTriggerLabel()}
            </ShadcnButton>
        )
    }

    const renderPopoverContent = () => {
        if (selectedCustomContract) {
            return (
                <CustomContractActionsMenu
                    onDelete={handleDelete}
                    onEdit={handleEditCustomContract}
                    showDeleteConfirmation={showDeleteConfirmation}
                    onRequestDeleteConfirmation={handleDeleteConfirmation}
                    onCancelDelete={handleCancelDelete}
                    disabled={!!disabled}
                    contractName={selectedCustomContract.number}
                />
            )
        }

        if (selectedDefaultContract) {
            return (
                <DefaultContractActionsMenu
                    contract={selectedDefaultContract}
                    disabled={!!disabled}
                    onSendToSelf={() =>
                        handleSendToSelf(selectedDefaultContract)
                    }
                    onEdit={handleEditDefaultContract}
                    onOpenGoogleDrive={() =>
                        handleOpenGoogleDriveFolder(selectedDefaultContract)
                    }
                    onDelete={handleDelete}
                    showDeleteConfirmation={showDeleteConfirmation}
                    onRequestDeleteConfirmation={handleDeleteConfirmation}
                    onCancelDelete={handleCancelDelete}
                />
            )
        }

        return (
            <ContractSelectionList
                contracts={draftContracts}
                canCreateContract={canCreateContract}
                createContractTooltip={createContractTooltip}
                createNewDocumentMessage={createNewDocumentMessage}
                onCreateContract={handleCreateContract}
                onSelectDefaultContract={handleSelectDefaultContract}
                onSelectCustomContract={handleSelectCustomContract}
            />
        )
    }

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <div className="relative">
                <PopoverTrigger
                    asChild
                    disabled={disabled || finalLoadingStatus}
                >
                    {renderTrigger()}
                </PopoverTrigger>

                <div className="absolute right-3 top-2 cursor-pointer">
                    {renderRightControl()}
                </div>
            </div>
            <PopoverContent
                className={cn('w-56 p-0', className)}
                align="start"
                sideOffset={4}
                style={{ maxHeight: '200px', overflow: 'scroll' }}
            >
                <Command shouldFilter={false}>
                    <CommandList className="max-h-[300px] overflow-y-scroll rounded-lg p-2">
                        {renderPopoverContent()}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
